//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { SamplingDeliveryMethod } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { CampaignRequestCurrencyField } from '@campaigns/components/campaigns-overview/CampaignRequestCurrencyField';
import { CampaignRequestLocaleField } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignRequestLocaleField';
import { CampaignRequestRadioField } from '@campaigns/components/shared/CampaignRequestRadioField';
import {
  getCouponCodeProviderOptions,
  getShippingManagerOptions,
} from '@campaigns/constants/sampling/SamplingCreateCampaignModalConstants';
import { isSingleRetailerPurchaseOption } from '@campaigns/utils/sampling/PrepaidCampaignUtils';
import {
  BodyBoldText,
  BodyText,
  CaptionText,
} from '@components/atoms/text/Text';
import { getClientName } from '@redux/selectors/getSessionInfo';

interface PrepaidLocationModalProps {
  deliveryMethod?: SamplingDeliveryMethod;
}

export const PrepaidLocationModal = ({
  deliveryMethod,
}: PrepaidLocationModalProps) => {
  const { getValues, setValue, watch } =
    useFormContext<SamplingCampaignRequest>();
  const clientName = useSelector(getClientName);

  const purchaseOptions = watch('purchaseOption');

  const availableCountryCodes = (purchaseOptions ?? [])
    .flatMap((po) => po.fulfillmentOptions ?? [])
    .flatMap((fo) => fo.availableCountryCodes ?? []);

  // Deduplicate country codes
  const uniqueCountryCodes = Array.from(new Set(availableCountryCodes));

  const isDigitalDelivery =
    deliveryMethod === SamplingDeliveryMethod.DigitalDelivery;

  const primaryOption = purchaseOptions?.[0];
  const isSingleRetailer = isSingleRetailerPurchaseOption(primaryOption);

  const shouldShowShippingManagerField =
    (deliveryMethod === SamplingDeliveryMethod.ShippedByBazaarvoice &&
      !isSingleRetailer) ||
    (deliveryMethod === SamplingDeliveryMethod.Shipped && isSingleRetailer);

  const handleWhoShipsProductChange = (value: string) => {
    if (value === 'myCompany') {
      const currentValues = getValues();
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        participantCount: undefined,
        packageWeight: undefined,
        packageInsert: undefined,
        boxType: undefined,
      });
    }
  };

  const handleCodeSupplierChange = (value: string) => {
    const currentValues = getValues();

    if (value === 'client') {
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        clientCodes: [],
        clientCodesSupplied: false,
        codeScope: undefined,
        // If there are multiple retailers, keep only the first one since
        // we don't support multiple retailers for client-provided codes
        retailers:
          currentValues.fulfillmentOptions?.retailers?.slice(0, 1) ?? [],
      });
    } else {
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        clientCodes: undefined,
        clientCodesSupplied: undefined,
        codeScope: undefined,
      });
    }
  };

  return (
    <Container>
      <BodyBoldText>
        {isDigitalDelivery
          ? `Complete details for your ${deliveryMethod} campaign:`
          : 'Complete details for your Shipped campaign:'}
      </BodyBoldText>
      <FieldsContainer>
        {shouldShowShippingManagerField && (
          <CampaignRequestRadioField
            label='Shipping manager'
            labelHelpText='Select whether you want Bazaarvoice to manage the shipping of products in the campaign, or to manage it yourself.'
            name='whoShipsProduct'
            onChange={handleWhoShipsProductChange}
            options={getShippingManagerOptions(clientName)}
          />
        )}
        <FieldSection>
          <BodyText>Country and Language</BodyText>
          <CaptionText>
            Location of participants and content language
          </CaptionText>
          <DropdownSection>
            <CampaignRequestLocaleField
              availableCountryCodes={uniqueCountryCodes}
              shouldHideLabel={true}
            />
          </DropdownSection>
        </FieldSection>
        <FieldSection>
          <CampaignRequestCurrencyField />
        </FieldSection>
        {isDigitalDelivery && (
          <CampaignRequestRadioField
            label='Code Provider'
            labelHelpText='Select whether you want Bazaarvoice to manage the coupon codes in the campaign, or to manage it yourself.'
            name='codeSupplier'
            onChange={handleCodeSupplierChange}
            options={getCouponCodeProviderOptions(clientName)}
          />
        )}
      </FieldsContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldSection = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  width: ${theme.mediumMeasure};
  margin-top: ${theme.spacings[3]};
`,
);

const DropdownSection = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  width: ${theme.smallMeasure};
`,
);
